import { ChevronRight, AlertCircle, CheckCircle, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDocuments } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useAccountVerification } from "@/hooks/useAccountVerification";

const DocumentsDashboard = () => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const { apiData } = useAccountVerification();
    const { document, isLoading } = useDocuments();
    const currentDate = new Date();

    // Get all document forms from backend preserving order across sessions
    // Support both shapes:
    // 1) apiData = { sessions: [{ forms: [...] }, ...] }
    // 2) apiData = [{ sessions: [{ forms: [...] }] }, ...]
    const documentForms: any[] = (() => {
        if (!apiData) return [];
        // shape 1
        if (Array.isArray(apiData?.sessions)) {
            return apiData.sessions.flatMap((s: any) => s.forms || []);
        }
        // shape 2
        if (Array.isArray(apiData)) {
            return apiData
                .flatMap((item: any) => (item?.sessions || []).flatMap((s: any) => s.forms || []))
                .filter(Boolean);
        }
        return [];
    })();


    // Helper function to get document data from profile
    const getDocumentData = (docType: string) => {
        return document?.documents?.find(
            (doc: any) => doc.docType?.toLowerCase() === docType?.toLowerCase()
        );
    };

    // Helper function to get expiry date for a document
    const getExpiryDate = (form: any) => {
        const doc = getDocumentData(form.header);
        if (!doc || !doc.data) return null;

        // Look for expiry date field in the document data
        const expiryField = form.fields.find((field: any) =>
            field.key?.toLowerCase().includes('expiry') ||
            field.key?.toLowerCase().includes('expirationdate') ||
            field.type?.toLowerCase() === 'date'
        );

        if (expiryField && doc.data[expiryField.key]) {
            return new Date(doc.data[expiryField.key]);
        }

        return null;
    };

    // Helper function to check if document is expired
    const isExpired = (form: any) => {

        const expiryDate = getExpiryDate(form);
        if (!expiryDate) return false;
        if (form?.header?.toLowerCase() === "vehicle inspection") {
            const inspectionValidityPeriod = 365;
            const issueDate = new Date(expiryDate);
            const calculatedExpiryDate = new Date(issueDate);
            calculatedExpiryDate.setDate(issueDate.getDate() + inspectionValidityPeriod);

            return calculatedExpiryDate < currentDate;
        }


        return expiryDate < currentDate;
    };


    // Get status for a document
    const getDocumentStatus = (form: any): {
        status: "pending" | "expired" | "verified" | "rejected" | "in-review";
        text: string;
    } => {

        const doc = getDocumentData(form.header);

        if (!doc || !doc.data) {
            return {
                status: "pending",
                text: "Please complete your documents verification"
            };
        }

        // Check if all required fields for this form are provided
        const requiredOk = (form.fields || []).every((f: any) => {
            if (!f?.required) return true;
            const v = doc.data[f.key];
            return v !== undefined && v !== null && String(v).trim() !== "";
        });

        if (!requiredOk) {
            return {
                status: "pending",
                text: "Please complete your documents verification"
            };
        }


        if (isExpired(form)) {
            return {
                status: "expired",
                text: "Expired - Please update"
            };
        }

        if (doc.rejectedReason) {
            return {
                status: "rejected",
                text: "Rejected - Action Required"
            };
        }

        return {
            status: "verified",
            text: "Verified ✓"
        };
    };

    // Determine if a form is considered "filled" enough to unlock the next one
    // Includes: required fields present AND not expired AND not rejected
    const isFormFilled = (form: any) => {
        const doc = getDocumentData(form.header);
        if (!doc || !doc.data) return false;
        // all required fields must be present and truthy
        const requiredOk = (form.fields || []).every((f: any) => {
            if (!f?.required) return true;
            const v = doc.data[f.key];
            return v !== undefined && v !== null && String(v).trim() !== "";
        });
        if (!requiredOk) return false;
        if (doc.rejectedReason) return false;
        if (isExpired(form)) return false;
        return true;
    };

    // Compute which forms should be visible.
    // Rule: only block progression when previous form is in "pending" status.
    // Expired or rejected should NOT block showing next forms.
    const visibleForms = (() => {


        const result: any[] = [];
        for (const f of documentForms) {
            result.push(f);
            const status = getDocumentStatus(f).status;
            if (status === "pending") break; // stop after including the first pending
        }
        return result;
    })();

    // Determine index of the first incomplete form in the full list


    const getDocumentDescription = (form: any, statusObj: any, doc: any) => {
        const formHeader = form.header?.toLowerCase();

        if (doc?.rejectedReason) {
            return doc.rejectedReason;
        }


        if (!isFormFilled(form)) {
            return "Please complete your documents verification";
        }
        if (statusObj.status === "expired") {
            return "Please complete your documents verification";
        }

        if (statusObj.status === "pending" || statusObj.status === "rejected") {
            return "Please complete your documents verification";
        }

        if (formHeader.includes("license") || formHeader.includes("licence")) {
            return "Confirm your identity";
        }

        if (formHeader.includes("vehicle registration")) {
            return "Information about the vehicle";
        }

        if (formHeader.includes("insurance")) {
            return "To prevent delays, ensures your photo is clear and includes all four corners";
        }

        if (formHeader.includes("inspection")) {
            return "Get your vehicle inspected and upload the form";
        }

        if (formHeader.includes("work") || formHeader.includes("eligibility")) {
            return "Driver must provide valid proof of work eligibility in order to drive with RapidoRide. Select your documents type from the dropdown, then upload a photo";
        }

        return form.description || "Confirm your identity";
    };

    // Handle navigation to form
    const handleDocumentClick = (form: any) => {
        navigate(`/form/${form?._id}`, {
            state: { formData: form, token, from: '/forms' }, replace: true
        });
    };

    if (isLoading) {
        return (
            <div className="p-4">
                <div className="text-gray-500">Loading documents...</div>
            </div>
        );
    }
    const handleBack = () => {
        navigate('/forms', { replace: true });
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
            {/* <div className="flex items-center justify-between w-full mb-6">
                <div onClick={handleBack} className="cursor-pointer">
                    <ChevronLeft />
                </div>
               
                <div className="w-6"></div>
            </div> */}
            <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-slate-900 text-black dark:text-white" >
                <div onClick={handleBack} className="cursor-pointer">
                    <ChevronLeft />
                </div>
                <div className="flex-1 text-center">
                    <h1 className='text-lg font-semibold'>Documents</h1>
                </div>
                <div className="w-6"></div>
            </div>
            {/* Documents List */}
            <div>
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500">Loading documents...</div>
                ) : documentForms.length === 0 ? (
                    <div className="p-8 text-center">
                        <p className="text-gray-500 font-medium">No document forms available</p>
                    </div>
                ) : (
                    visibleForms.map((form: any, index: number) => {
                        const statusObj = getDocumentStatus(form);
                        const doc = getDocumentData(form.header);
                        const expiryDate = getExpiryDate(form);

                        // Determine if this is an error state
                        const isError = statusObj.status === "expired" || statusObj.status === "rejected" || statusObj.status === "pending";
                        // Determine if this is the item immediately before the first incomplete form

                        return (
                            <div
                                key={`${form._id}-${index}`}
                                className="flex items-start gap-3 p-4 border-b border-gray-100 dark:border-slate-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                onClick={() => handleDocumentClick(form)}
                            >
                                {/* Status Icon */}
                                <div className="shrink-0 mt-1">
                                    {isError ? (
                                        <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                                            <AlertCircle className="w-4 h-4 text-white" />
                                        </div>
                                    ) : (
                                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                            <CheckCircle className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    {/* Document Title */}
                                    <h3 className="text-base font-medium text-gray-900 dark:text-white mb-0.5">
                                        {form.header}
                                    </h3>

                                    {/* Status Text */}
                                    {statusObj.status === "expired" && (
                                        <p className="text-sm font-medium text-red-600 mb-1">Expired</p>
                                    )}
                                    {(statusObj.status === "pending") && (
                                        <p className="text-sm font-medium text-red-600 mb-1">
                                            Please complete your documents verification
                                        </p>
                                    )}
                                    {statusObj.status === "rejected" && (
                                        <p className="text-sm font-medium text-red-600 mb-1">
                                            Rejected
                                        </p>
                                    )}

                                    {/* Description */}
                                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                        {form.description || getDocumentDescription(form, statusObj, doc)}
                                    </p>

                                    {/* Expiry Date - only for verified documents */}
                                    {expiryDate && statusObj.status === "verified" && (
                                        <p className="text-xs text-gray-500 mt-2">
                                            Expires: {expiryDate.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    )}
                                </div>

                                {/* Arrow Icon */}
                                <div className="shrink-0">
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default DocumentsDashboard;
